// src/hooks/useTeacherData.ts
import { useState, useCallback, useEffect } from 'react';
import { useAuth } from './useAuth';
import {
  studentsService,
  classesService,
  paymentsService,
} from '../services/firestoreService';
import { Student, Class, Payment } from '../types/firestore';
import { authDiagnostic } from '../services/firebase';

interface TeacherStats {
  totalStudents: number;
  activeStudents: number;
  totalClasses: number;
  todaySessions: number;
  monthlyRevenue: number;
  pendingTasks: number;
  completionRate: number;
}

interface UpcomingSession {
  id: string;
  subject: string;
  grade: string;
  time: string;
  students: number;
  classId?: string;
}

interface PendingTask {
  type: 'assignment' | 'payment' | 'feedback' | 'report';
  title: string;
  count: number;
  priority: 'high' | 'medium' | 'low';
  id?: string;
}

export const useTeacherData = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<TeacherStats | null>(null);
  const [upcomingSessions, setUpcomingSessions] = useState<UpcomingSession[]>(
    []
  );
  const [pendingTasks, setPendingTasks] = useState<PendingTask[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [classes, setClasses] = useState<Class[]>([]);
  const [recentPayments, setRecentPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const teacherId = user?.role === 'teacher' ? user.id : null;

  const fetchTeacherData = useCallback(async () => {
    if (!teacherId) {
      setStats(null);
      setUpcomingSessions([]);
      setPendingTasks([]);
      setStudents([]);
      setClasses([]);
      setRecentPayments([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    authDiagnostic('FIRESTORE_TEACHER_DATA_REQUEST_START', { teacherId });

    try {
      // جلب البيانات الأساسية من الخدمات
      const [teacherStudents, teacherClasses] = await Promise.all([
        // جلب الطلاب المرتبطين بالمدرس
        studentsService.getByTeacherId(teacherId),
        // جلب الفصول التي يدرسها
        classesService.getByTeacherId(teacherId),
      ]);

      // جلب المدفوعات الحديثة مفلترة بـ teacherId مباشرة
      const teacherPayments = await paymentsService.getByTeacherId(teacherId);

      // تصفية المدفوعات لهذا الشهر
      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();
      const monthlyPayments = teacherPayments.filter((p: Payment) => {
        // تحويل Timestamp إلى Date
        const paymentDate =
          p.createdAt &&
          typeof p.createdAt === 'object' &&
          'toDate' in p.createdAt
            ? p.createdAt.toDate()
            : new Date();
        return (
          paymentDate.getMonth() === currentMonth &&
          paymentDate.getFullYear() === currentYear
        );
      });

      setStudents(teacherStudents);
      setClasses(teacherClasses);
      setRecentPayments(monthlyPayments);

      // حساب الإحصائيات
      const totalStudents = teacherStudents.length;
      const activeStudents = teacherStudents.filter(
        (s: Student) => s.isActive
      ).length;
      const totalClasses = teacherClasses.length;

      // حساب إيرادات الشهر
      const monthlyRevenue = monthlyPayments
        .filter((p: Payment) => p.status === 'paid')
        .reduce((sum: number, p: Payment) => sum + p.amount, 0);

      // حصص اليوم وفق جدول الصفوف النشطة
      const today = new Date().getDay();
      const todaySessions = teacherClasses.filter(
        (teacherClass: Class) =>
          teacherClass.isActive && teacherClass.schedule?.dayOfWeek === today
      ).length;
      const pendingPayments = teacherPayments.filter(
        (payment: Payment) => payment.status === 'pending'
      ).length;

      // حساب معدل إنجاز الحصص من البيانات المخزنة
      const totalPlannedSessions = teacherClasses.reduce(
        (sum: number, teacherClass: Class) =>
          sum + (teacherClass.totalSessions || 0),
        0
      );
      const totalCompletedSessions = teacherClasses.reduce(
        (sum: number, teacherClass: Class) =>
          sum + (teacherClass.completedSessions || 0),
        0
      );
      const completionRate =
        totalPlannedSessions > 0
          ? Math.min(
              100,
              Math.round(
                (totalCompletedSessions / totalPlannedSessions) * 100
              )
            )
          : 0;

      const calculatedStats: TeacherStats = {
        totalStudents,
        activeStudents,
        totalClasses,
        todaySessions,
        monthlyRevenue,
        pendingTasks: pendingPayments,
        completionRate,
      };

      setStats(calculatedStats);

      // إعداد الجلسات القادمة
      const sessions: UpcomingSession[] = teacherClasses
        .filter((cls: Class) => cls.isActive)
        .sort(
          (first: Class, second: Class) =>
            (first.schedule?.dayOfWeek || 0) -
            (second.schedule?.dayOfWeek || 0)
        )
        .slice(0, 3)
        .map((cls: Class) => ({
          id: cls.id!,
          subject: cls.subject,
          grade: cls.grade || 'غير محدد',
          time: cls.schedule?.startTime || 'غير محدد',
          students: cls.enrolledStudents?.length || 0,
          classId: cls.id,
        }));

      setUpcomingSessions(sessions);

      // إعداد المهام المعلقة - استخدام البيانات الحقيقية فقط
      const tasks: PendingTask[] = [
        {
          type: 'payment' as const,
          title: 'دفعات متأخرة',
          count: pendingPayments,
          priority: 'medium' as const,
        }
      ].filter(task => task.count > 0);

      setPendingTasks(tasks);
    } catch (err) {
      authDiagnostic('FIRESTORE_TEACHER_DATA_REQUEST_RESULT', {
        success: false,
        errorCategory:
          err && typeof err === 'object' && 'code' in err
            ? String((err as { code?: unknown }).code)
            : 'unknown',
      });
      console.error('Error fetching teacher data:', err);
      setError('حدث خطأ في جلب البيانات');
    } finally {
      setLoading(false);
    }
  }, [teacherId]);

  // تحديث البيانات عند تغيير المستخدم
  useEffect(() => {
    fetchTeacherData();
  }, [fetchTeacherData]);

  // دالة لتحديث البيانات يدوياً
  const refreshData = () => {
    fetchTeacherData();
  };

  // دالة لإضافة طالب جديد
  const addStudent = async (
    studentData: Omit<Student, 'id' | 'createdAt' | 'updatedAt'>
  ) => {
    if (!user?.id) {
      throw new Error('Teacher authentication is required');
    }
    try {
      const studentId = await studentsService.create({
        ...studentData,
        teacherId: user.id,
      });

      // جلب الطالب الجديد
      const newStudent = await studentsService.getTenantById(user.id, studentId);
      if (newStudent) {
        // تحديث القائمة المحلية
        setStudents(prev => [...prev, newStudent]);

        // تحديث الإحصائيات
        if (stats) {
          setStats(prev =>
            prev
              ? {
                  ...prev,
                  totalStudents: prev.totalStudents + 1,
                  activeStudents: prev.activeStudents + 1,
                }
              : null
          );
        }
      }

      return newStudent;
    } catch (err) {
      console.error('Error adding student:', err);
      throw new Error('فشل في إضافة الطالب');
    }
  };

  // دالة لإضافة فصل جديد
  const addClass = async (
    classData: Omit<Class, 'id' | 'createdAt' | 'updatedAt'>
  ) => {
    try {
      const classId = await classesService.create({
        ...classData,
        teacherId: user?.id || '',
      });

      // جلب الفصل الجديد
      const newClass = user?.id
        ? await classesService.getTenantById(user.id, classId)
        : null;
      if (newClass) {
        // تحديث القائمة المحلية
        setClasses(prev => [...prev, newClass]);
      }

      return newClass;
    } catch (err) {
      console.error('Error adding class:', err);
      throw new Error('فشل في إضافة الفصل');
    }
  };

  // دالة لتسجيل دفعة جديدة
  const addPayment = async (
    paymentData: Omit<Payment, 'id' | 'createdAt' | 'updatedAt'>
  ) => {
    try {
      const paymentId = await paymentsService.create({
        ...paymentData,
        teacherId: user?.id || '',
      });

      // جلب الدفعة الجديدة
      const newPayment = user?.id
        ? await paymentsService.getTenantById(user.id, paymentId)
        : null;
      if (newPayment) {
        // تحديث القائمة المحلية
        setRecentPayments(prev => [newPayment, ...prev]);

        // تحديث الإحصائيات
        if (stats && newPayment.status === 'paid') {
          setStats(prev =>
            prev
              ? {
                  ...prev,
                  monthlyRevenue: prev.monthlyRevenue + newPayment.amount,
                }
              : null
          );
        }
      }

      return newPayment;
    } catch (err) {
      console.error('Error adding payment:', err);
      throw new Error('فشل في تسجيل الدفعة');
    }
  };

  return {
    // البيانات
    stats,
    upcomingSessions,
    pendingTasks,
    students,
    classes,
    recentPayments,

    // حالات التحميل والخطأ
    loading,
    error,

    // دوال الإجراءات
    refreshData,
    addStudent,
    addClass,
    addPayment,
  };
};

export default useTeacherData;
